import React, { useEffect, useState, useMemo } from 'react';
import { Spinner, SpinnerSize, mergeStyleSets } from '@fluentui/react';
import { Check, ArrowRight } from '@phosphor-icons/react';
import { datasetService } from '../../services/datasetService';
import AccessSummaryBanner from '../common/AccessSummaryBanner';
import type { DepartmentSummary } from '../../types/dataset.types';

interface Props {
  onStart: (selectedIds: string[], names: string[]) => void;
  onLoadSaved: () => void;
}

// CSS-only dataset icon palette — tinted background + saturated text.
// Pairs are picked deterministically from the dataset name so the same
// dataset always gets the same colour.
const ICON_PALETTE: { bg: string; fg: string }[] = [
  { bg: '#ECFDF5', fg: '#15803D' }, // green
  { bg: '#EFF6FF', fg: '#1D4ED8' }, // blue
  { bg: '#FFF7ED', fg: '#C2410C' }, // orange
  { bg: '#F5F3FF', fg: '#6D28D9' }, // purple
  { bg: '#FEF2F2', fg: '#DC2626' }, // red
  { bg: '#F0FDFA', fg: '#0D9488' }, // teal
  { bg: '#FEFCE8', fg: '#A16207' }, // amber
  { bg: '#FDF2F8', fg: '#BE185D' }, // pink
];

function iconColors(name: string): { bg: string; fg: string } {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash + name.charCodeAt(i)) % 997;
  return ICON_PALETTE[hash % ICON_PALETTE.length];
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter((w) => /[a-z0-9]/i.test(w.charAt(0)));
  if (words.length >= 2) return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  return name.trim().slice(0, 2).toUpperCase();
}

const s = mergeStyleSets({
  wrap: {
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: '100%',
    background: '#EEEFF2',
  },
  // Hero banner — brand-green gradient with a soft radial highlight.
  hero: {
    position: 'relative' as const,
    overflow: 'hidden',
    padding: '40px 48px 36px',
    background: 'linear-gradient(155deg, #1a5c26 0%, #2e873d 60%, #3d9e4d 100%)',
    selectors: {
      '::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background:
          'radial-gradient(ellipse 70% 90% at 100% 40%, rgba(255,255,255,0.07), transparent 55%)',
      },
    },
  },
  heroInner: {
    position: 'relative' as const,
    maxWidth: 600,
  },
  heroEyebrow: {
    fontSize: 9.5,
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase' as const,
    color: 'rgba(255,255,255,0.5)',
    marginBottom: 12,
  },
  heroTitle: {
    fontSize: 26,
    fontWeight: 800,
    color: '#FFFFFF',
    letterSpacing: '-0.025em',
    lineHeight: '1.2',
    marginBottom: 8,
  },
  heroText: {
    fontSize: 13.5,
    color: 'rgba(255,255,255,0.65)',
    lineHeight: '1.65',
    marginBottom: 22,
  },
  heroBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 18px',
    borderRadius: 6,
    background: '#FFFFFF',
    color: '#1a5c26',
    fontSize: 12.5,
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
    transition: 'background 0.12s ease',
    selectors: {
      ':hover': { background: '#F0F0F0' },
    },
  },
  body: {
    flex: 1,
    padding: '24px 48px',
    maxWidth: 1180,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box' as const,
  },
  tabRow: {
    display: 'inline-flex',
    gap: 2,
    background: '#FFFFFF',
    border: '1px solid #E4E4E8',
    borderRadius: 7,
    padding: 3,
    marginBottom: 22,
  },
  tab: {
    padding: '6px 18px',
    borderRadius: 5,
    fontSize: 12,
    fontWeight: 600,
    color: '#6B7280',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  tabActive: {
    background: '#2e873d',
    color: '#FFFFFF',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: 12,
  },
  card: {
    position: 'relative' as const,
    background: '#FFFFFF',
    border: '1.5px solid #E4E4E8',
    borderRadius: 10,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.18s',
    selectors: {
      ':hover': { borderColor: '#2e873d', boxShadow: '0 0 0 3px rgba(46,135,61,0.08)' },
    },
  },
  cardSelected: {
    borderColor: '#2e873d',
    background: '#f8fdf8',
    boxShadow: '0 0 0 3px rgba(46,135,61,0.09)',
  },
  cardDisabled: {
    opacity: 0.45,
    cursor: 'default',
    selectors: {
      ':hover': { borderColor: '#E4E4E8', boxShadow: 'none' },
    },
  },
  cardTop: {
    display: 'flex',
    gap: 12,
    padding: '16px 16px 12px',
    borderBottom: '1px solid #F3F4F6',
  },
  icon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.02em',
  },
  name: {
    fontSize: 13,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 3,
  },
  desc: {
    fontSize: 11.5,
    color: '#6B7280',
    lineHeight: '1.5',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
  },
  cardFoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '9px 16px',
  },
  meta: {
    fontSize: 11,
    color: '#9CA3AF',
  },
  badge: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: '0.08em',
    padding: '2px 8px',
    borderRadius: 3,
  },
  badgePublic: {
    background: '#EFF6FF',
    color: '#1D4ED8',
  },
  badgeGoverned: {
    background: '#F5F3FF',
    color: '#6D28D9',
  },
  check: {
    position: 'absolute' as const,
    top: 12,
    right: 12,
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: '#2e873d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  empty: {
    gridColumn: '1 / -1',
    textAlign: 'center' as const,
    padding: 40,
    color: '#747474',
    fontSize: 13,
  },
  // Sticky footer CTA — stays pinned to the bottom while the grid scrolls.
  foot: {
    position: 'sticky' as const,
    bottom: 0,
    zIndex: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '14px 48px',
    background: '#FFFFFF',
    borderTop: '1px solid #E4E4E8',
    boxShadow: '0 -2px 10px rgba(0,0,0,0.05)',
  },
  footInfo: {
    fontSize: 13,
    color: '#374151',
    selectors: {
      '& b': { color: '#111827', fontWeight: 700 },
    },
  },
  startBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 28px',
    borderRadius: 7,
    background: '#2e873d',
    color: '#FFFFFF',
    border: 'none',
    fontSize: 13.5,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(46,135,61,0.25)',
    transition: 'background 0.12s ease',
    selectors: {
      ':hover': { background: '#256d31' },
      ':disabled': { opacity: 0.4, cursor: 'default', boxShadow: 'none' },
    },
  },
});

type BuilderTier = 'public' | 'governed';

export default function DatasetPicker({ onStart, onLoadSaved }: Props) {
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [activeTier, setActiveTier] = useState<BuilderTier>('public');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const depts = await datasetService.getDatasets();
        if (alive) setDepartments(depts);
      } catch (err) {
        console.error('Failed to load departments:', err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const accessibleDepts = useMemo(
    () => departments.filter((d) => d.hasAccess && d.status === 'active'),
    [departments]
  );

  // Per-tier counts drive the empty-state copy. Mapping: classification
  // 'OFFICIAL' is public (PostgreSQL-backed); anything else is governed
  // (Fabric-backed).
  const visibleDepts = useMemo(
    () =>
      accessibleDepts.filter((d) =>
        activeTier === 'public'
          ? d.classification === 'OFFICIAL'
          : d.classification !== 'OFFICIAL'
      ),
    [accessibleDepts, activeTier]
  );

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleStart = () => {
    const ids = Array.from(selected);
    const names = ids.map((id) => departments.find((d) => d.id === id)?.department || id);
    onStart(ids, names);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <Spinner size={SpinnerSize.large} label="Loading datasets..." />
      </div>
    );
  }

  return (
    <div className={s.wrap}>
      {/* Hero banner */}
      <div className={s.hero}>
        <div className={s.heroInner}>
          <div className={s.heroEyebrow}>Dashboard Builder</div>
          <div className={s.heroTitle}>What do you want to explore today?</div>
          <div className={s.heroText}>
            Choose one or more datasets, then describe your dashboard in plain English.
            The AI builds the charts, KPIs and tables.
          </div>
          <button className={s.heroBtn} onClick={onLoadSaved}>
            Open Saved Dashboard
          </button>
        </div>
      </div>

      {/* Body */}
      <div className={s.body}>
        <AccessSummaryBanner kind="builder" style={{ maxWidth: 'fit-content', margin: '0 0 16px' }} />

        <div className={s.tabRow}>
          <button
            type="button"
            className={`${s.tab} ${activeTier === 'public' ? s.tabActive : ''}`}
            onClick={() => setActiveTier('public')}
          >
            Public
          </button>
          <button
            type="button"
            className={`${s.tab} ${activeTier === 'governed' ? s.tabActive : ''}`}
            onClick={() => setActiveTier('governed')}
          >
            Governed
          </button>
        </div>

        <div className={s.grid}>
          {visibleDepts.map((dept) => {
            const isSelected = selected.has(dept.id);
            const isPublic = dept.classification === 'OFFICIAL';
            const colors = iconColors(dept.department);
            return (
              <div
                key={dept.id}
                className={`${s.card} ${isSelected ? s.cardSelected : ''}`}
                onClick={() => toggle(dept.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(dept.id); }}
              >
                {isSelected && (
                  <div className={s.check}>
                    <Check size={12} color="#FFFFFF" weight="bold" />
                  </div>
                )}

                <div className={s.cardTop}>
                  <div className={s.icon} style={{ background: colors.bg, color: colors.fg }}>
                    {initials(dept.department)}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className={s.name}>{dept.department}</div>
                    <div className={s.desc}>{dept.description}</div>
                  </div>
                </div>

                <div className={s.cardFoot}>
                  <span className={s.meta}>
                    {dept.tableCount > 0 && `${dept.tableCount} table${dept.tableCount !== 1 ? 's' : ''}`}
                    {dept.tableCount > 0 && dept.totalRows > 0 && ' · '}
                    {dept.totalRows > 0 && `${dept.totalRows.toLocaleString()} rows`}
                  </span>
                  <span className={`${s.badge} ${isPublic ? s.badgePublic : s.badgeGoverned}`}>
                    {isPublic ? 'PUBLIC' : 'GOVERNED'}
                  </span>
                </div>
              </div>
            );
          })}

          {visibleDepts.length === 0 && (
            <div className={s.empty}>
              {accessibleDepts.length === 0
                ? 'No accessible datasets found. Request access from the Datasets page.'
                : activeTier === 'public'
                  ? 'No public datasets available.'
                  : 'No governed datasets available. Request access from the Datasets page.'}
            </div>
          )}
        </div>
      </div>

      {/* Sticky footer CTA */}
      <div className={s.foot}>
        <span className={s.footInfo}>
          {selected.size === 0 ? (
            'Select one or more datasets to begin'
          ) : (
            <>
              <b>{selected.size}</b> dataset{selected.size !== 1 ? 's' : ''} selected
            </>
          )}
        </span>
        <button
          className={s.startBtn}
          disabled={selected.size === 0}
          onClick={handleStart}
        >
          Start Building
          <ArrowRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
